import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PlaylistItem {
  id: string;
  playlist_id: string;
  content_item_id: string;
  position: number;
  duration_override: number | null;
  content_item: {
    id: string;
    name: string;
    content_type: string;
    storage_path: string | null;
    external_url: string | null;
    thumbnail_url: string | null;
    duration_seconds: number;
  };
}

export interface Playlist {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  loop_mode: 'continuous' | 'once' | 'shuffle';
  transition_type: 'fade' | 'slide' | 'zoom' | 'none';
  transition_duration_ms: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  items?: PlaylistItem[];
  item_count?: number;
  total_duration?: number;
}

export interface CreatePlaylistInput {
  name: string;
  description?: string;
  loop_mode?: 'continuous' | 'once' | 'shuffle';
  transition_type?: 'fade' | 'slide' | 'zoom' | 'none';
  transition_duration_ms?: number;
}

export interface UpdatePlaylistInput extends Partial<CreatePlaylistInput> {
  is_active?: boolean;
}

export function useSignagePlaylists(businessId: string) {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlaylists = useCallback(async () => {
    try {
      setError(null);

      // Fetch playlists with item count
      const { data: playlistsData, error: playlistsError } = await supabase
        .from('signage_playlists')
        .select(`
          *,
          signage_playlist_items (
            id,
            position,
            duration_override,
            signage_content_items (
              id,
              name,
              duration_seconds
            )
          )
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (playlistsError) throw playlistsError;

      // Calculate item count and total duration for each playlist
      const playlistsWithStats = (playlistsData || []).map((playlist: any) => {
        const items = playlist.signage_playlist_items || [];
        const totalDuration = items.reduce((sum: number, item: any) => {
          const duration = item.duration_override || item.signage_content_items?.duration_seconds || 10;
          return sum + duration;
        }, 0);

        return {
          ...playlist,
          item_count: items.length,
          total_duration: totalDuration,
          items: undefined, // Don't include full items in list view
          signage_playlist_items: undefined,
        };
      });

      setPlaylists(playlistsWithStats);
    } catch (err: any) {
      console.error('Failed to fetch playlists:', err);
      setError(err.message || 'Failed to load playlists');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  const createPlaylist = useCallback(async (input: CreatePlaylistInput): Promise<Playlist | null> => {
    try {
      const { data, error } = await supabase
        .from('signage_playlists')
        .insert({
          business_id: businessId,
          name: input.name,
          description: input.description || null,
          loop_mode: input.loop_mode || 'continuous',
          transition_type: input.transition_type || 'fade',
          transition_duration_ms: input.transition_duration_ms || 500,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Playlist created');
      await fetchPlaylists();
      return data;
    } catch (err: any) {
      console.error('Failed to create playlist:', err);
      toast.error('Failed to create playlist');
      return null;
    }
  }, [businessId, fetchPlaylists]);

  const updatePlaylist = useCallback(async (playlistId: string, input: UpdatePlaylistInput): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_playlists')
        .update({
          name: input.name,
          description: input.description,
          loop_mode: input.loop_mode,
          transition_type: input.transition_type,
          transition_duration_ms: input.transition_duration_ms,
          is_active: input.is_active,
        })
        .eq('id', playlistId);

      if (error) throw error;

      toast.success('Playlist updated');
      await fetchPlaylists();
      return true;
    } catch (err: any) {
      console.error('Failed to update playlist:', err);
      toast.error('Failed to update playlist');
      return false;
    }
  }, [fetchPlaylists]);

  const deletePlaylist = useCallback(async (playlistId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_playlists')
        .delete()
        .eq('id', playlistId);

      if (error) throw error;

      toast.success('Playlist deleted');
      await fetchPlaylists();
      return true;
    } catch (err: any) {
      console.error('Failed to delete playlist:', err);
      toast.error('Failed to delete playlist');
      return false;
    }
  }, [fetchPlaylists]);

  const getPlaylistWithItems = useCallback(async (playlistId: string): Promise<Playlist | null> => {
    try {
      const { data, error } = await supabase
        .from('signage_playlists')
        .select(`
          *,
          signage_playlist_items (
            id,
            playlist_id,
            content_item_id,
            position,
            duration_override,
            signage_content_items (
              id,
              name,
              content_type,
              storage_path,
              external_url,
              thumbnail_url,
              duration_seconds
            )
          )
        `)
        .eq('id', playlistId)
        .single();

      if (error) throw error;

      // Transform the data
      const items = (data.signage_playlist_items || [])
        .sort((a: any, b: any) => a.position - b.position)
        .map((item: any) => ({
          id: item.id,
          playlist_id: item.playlist_id,
          content_item_id: item.content_item_id,
          position: item.position,
          duration_override: item.duration_override,
          content_item: item.signage_content_items,
        }));

      return {
        ...data,
        items,
        signage_playlist_items: undefined,
      };
    } catch (err: any) {
      console.error('Failed to fetch playlist:', err);
      return null;
    }
  }, []);

  const addItemToPlaylist = useCallback(async (
    playlistId: string,
    contentItemId: string,
    position?: number
  ): Promise<boolean> => {
    try {
      // Get current max position if not specified
      if (position === undefined) {
        const { data: existingItems } = await supabase
          .from('signage_playlist_items')
          .select('position')
          .eq('playlist_id', playlistId)
          .order('position', { ascending: false })
          .limit(1);

        position = (existingItems?.[0]?.position ?? -1) + 1;
      }

      const { error } = await supabase
        .from('signage_playlist_items')
        .insert({
          playlist_id: playlistId,
          content_item_id: contentItemId,
          position,
        });

      if (error) throw error;

      await fetchPlaylists();
      return true;
    } catch (err: any) {
      console.error('Failed to add item to playlist:', err);
      toast.error('Failed to add item to playlist');
      return false;
    }
  }, [fetchPlaylists]);

  const removeItemFromPlaylist = useCallback(async (itemId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_playlist_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      await fetchPlaylists();
      return true;
    } catch (err: any) {
      console.error('Failed to remove item from playlist:', err);
      toast.error('Failed to remove item');
      return false;
    }
  }, [fetchPlaylists]);

  const updateItemDuration = useCallback(async (itemId: string, duration: number | null): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('signage_playlist_items')
        .update({ duration_override: duration })
        .eq('id', itemId);

      if (error) throw error;
      return true;
    } catch (err: any) {
      console.error('Failed to update item duration:', err);
      return false;
    }
  }, []);

  const reorderPlaylistItems = useCallback(async (
    playlistId: string,
    itemIds: string[]
  ): Promise<boolean> => {
    try {
      // Update positions for all items
      const updates = itemIds.map((id, index) => ({
        id,
        position: index,
      }));

      // Use upsert to update all positions
      for (const update of updates) {
        const { error } = await supabase
          .from('signage_playlist_items')
          .update({ position: update.position })
          .eq('id', update.id);

        if (error) throw error;
      }

      return true;
    } catch (err: any) {
      console.error('Failed to reorder items:', err);
      toast.error('Failed to reorder items');
      return false;
    }
  }, []);

  return {
    playlists,
    loading,
    error,
    fetchPlaylists,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    getPlaylistWithItems,
    addItemToPlaylist,
    removeItemFromPlaylist,
    updateItemDuration,
    reorderPlaylistItems,
  };
}
